import { apiSlice } from './apiSlice';
import { routeDeleteReadModelInvalidatesTags } from './routeDeleteReadModelTags';
import { notificationListTag, notificationsApi } from './notificationsApi';
import {
  Route,
  CreateRouteRequest,
  RouteFilters,
  RouteEvent,
  ApplicationCargo,
  ApiResponse
} from '../../types';
import type { RouteViewModel } from '../../types/routeViewModel';
import type {
  ExecutionDashboardSlice,
  ExecutionEventRow,
  ExecutionProgressHydration,
  StopExecutionAction,
  CargoQuantityUpdate
} from '../../features/executionOperations/types';
import type { ExecutionIncident } from '../../features/routes/execution/incidentFacade';
import type { OperationalCompletionSnapshotPayload } from '../../features/operationalDomain/completion/completionSnapshot';
import type {
  ProcessQueuePayload,
  RouteOperationalProcessesPayload
} from '../../features/operationalDomain/processes/operationalProcessTypes';
import type { OperationalInboxSummary } from '../../features/operationalDomain/workload/operationalInboxTypes';

type RouteStopsMutationMeta = {
  liveTopology?: {
    allowed?: boolean;
    forbidden?: boolean;
    requiresConfirmation?: boolean;
    recalculationRequired?: boolean;
    policyTier?: string;
    reasonCodes?: string[];
    affectedSubsystems?: string[];
  };
  orchestration?: unknown;
};

/** Array of saved stops with optional M1.1b/M1.2 mutation metadata attached. */
export type RouteStopsUpsertResult = any[] &
  RouteStopsMutationMeta & {
    executionSnapshot?: {
      frozenAt: number;
      stops: Array<Record<string, unknown>>;
      allocations: Array<Record<string, unknown>>;
    };
  };

interface Contractor {
  id: number;
  name: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  inn?: string;
  roles: string[];
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export const routesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    
    // Получить все маршруты с фильтрами и пагинацией
    getAllRoutes: builder.query<{routes: Route[], pagination: any}, RouteFilters & {page?: number, limit?: number, search?: string}>({
      query: (filters = {}) => {
        const params = new URLSearchParams();
        
        if (filters.transportType) params.append('transportType', filters.transportType);
        if (filters.status) params.append('status', filters.status);
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
        if (filters.applicationId) params.append('applicationId', filters.applicationId.toString());
        if (filters.companyOnly) params.append('companyOnly', filters.companyOnly.toString());
        if ((filters as any).responsibleLogistId) params.append('responsibleLogistId', String((filters as any).responsibleLogistId));
        if ((filters as any).carrierId) params.append('carrierId', String((filters as any).carrierId));
        // driverId: явно в query, иначе undefined пропадёт при JSON.stringify(арг) и сольёт кэш с «всеми маршрутами»
        if (filters.driverId != null && Number.isFinite(Number(filters.driverId))) {
          params.append('driverId', String(Number(filters.driverId)));
        }
        if (filters.search) params.append('search', filters.search);
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.limit) params.append('limit', filters.limit.toString());
        
        return {
          url: `/routes?${params.toString()}`,
          method: 'GET'
        };
      },
      // Стабильный ключ кэша: undefined-поля не должны сливать разные выборки
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = (queryArgs || {}) as RouteFilters & { page?: number; limit?: number; search?: string };
        const key = {
          t: a.transportType,
          st: a.status,
          df: a.dateFrom,
          dt: a.dateTo,
          app: a.applicationId,
          co: a.companyOnly,
          rl: (a as { responsibleLogistId?: number }).responsibleLogistId,
          ca: a.carrierId,
          dr: a.driverId != null && Number.isFinite(Number(a.driverId)) ? Number(a.driverId) : null,
          s: a.search,
          p: a.page,
          l: a.limit
        };
        return `${endpointName}(${JSON.stringify(key)})`;
      },
      transformResponse: (response: ApiResponse<{routes: Route[], pagination: any}>) => response.data!,
      providesTags: ['Route']
    }),

    // Получить стопы маршрута
    getRouteStops: builder.query<any[], number>({
      query: (routeId: number) => ({
        url: `/routes/${routeId}/stops`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<any[]>) => response.data || [],
      providesTags: (result, error, routeId) => [{ type: 'Route', id: `stops-${routeId}` }]
    }),

    // Сохранить (заменить) стопы маршрута
  upsertRouteStops: builder.mutation<
    RouteStopsUpsertResult,
    {
      routeId: number;
      stops: any[];
      allocationLinks?: Array<{ fromLoadingPosition: number; toUnloadingPosition: number }>;
      executionSnapshot?: Record<string, unknown>;
      executionSessionId?: string;
      mutationReason?: string;
      expectedUpdatedAt?: string;
      confirmationProvided?: boolean;
    }
  >({
      query: ({
        routeId,
        stops,
        allocationLinks,
        executionSnapshot,
        executionSessionId,
        mutationReason,
        expectedUpdatedAt,
        confirmationProvided
      }) => ({
        url: `/routes/${routeId}/stops`,
        method: 'PUT',
        body: {
          stops,
          ...(allocationLinks && allocationLinks.length > 0 ? { allocationLinks } : {}),
          ...(executionSnapshot ? { executionSnapshot } : {}),
          ...(executionSessionId ? { executionSessionId } : {}),
          ...(mutationReason ? { mutationReason } : {}),
          ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}),
          ...(confirmationProvided ? { confirmationProvided: true } : {})
        }
      }),
      transformResponse: (
        response: ApiResponse<any[]> &
          RouteStopsMutationMeta & {
            executionSnapshot?: RouteStopsUpsertResult['executionSnapshot'];
          }
      ) => {
        const stops = response.data || [];
        return Object.assign([...stops], {
          liveTopology: response.liveTopology,
          orchestration: response.orchestration,
          executionSnapshot: response.executionSnapshot
        }) as RouteStopsUpsertResult;
      },
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        { type: 'Route', id: `stops-${routeId}` },
        { type: 'Route', id: `execution-${routeId}` },
        { type: 'Route', id: `execution-dash-${routeId}` },
        'Cargo',
        'Application'
      ]
    }),

    // Удалить стоп маршрута
    deleteRouteStop: builder.mutation<
      RouteStopsMutationMeta,
      {
        routeId: number;
        stopId: number;
        mutationReason?: string;
        confirmationProvided?: boolean;
      }
    >({
      query: ({ routeId, stopId, mutationReason, confirmationProvided }) => ({
        url: `/routes/${routeId}/stops/${stopId}`,
        method: 'DELETE',
        body: {
          ...(mutationReason ? { mutationReason } : {}),
          ...(confirmationProvided ? { confirmationProvided: true } : {})
        }
      }),
      transformResponse: (response: ApiResponse<unknown> & RouteStopsMutationMeta) => ({
        liveTopology: response.liveTopology,
        orchestration: response.orchestration
      }),
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        { type: 'Route', id: `stops-${routeId}` },
        { type: 'Route', id: `execution-${routeId}` },
        { type: 'Route', id: `execution-dash-${routeId}` },
        'Cargo',
        'Application'
      ]
    }),

    // Погрузка на стопе маршрута
    loadRouteStop: builder.mutation<any, { routeId: number; stopId: number; actualAt?: string }>({
      query: ({ routeId, stopId, actualAt }) => ({
        url: `/routes/${routeId}/stops/${stopId}/load`,
        method: 'POST',
        body: actualAt ? { actualAt } : undefined
      }),
      transformResponse: (response: ApiResponse<any>) => response.data || response,
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        { type: 'Route', id: `stops-${routeId}` },
        'Cargo',
        'Application'
      ]
    }),

    // Выгрузка на стопе маршрута
    unloadRouteStop: builder.mutation<any, { routeId: number; stopId: number; actualAt?: string }>({
      query: ({ routeId, stopId, actualAt }) => ({
        url: `/routes/${routeId}/stops/${stopId}/unload`,
        method: 'POST',
        body: actualAt ? { actualAt } : undefined
      }),
      transformResponse: (response: ApiResponse<any>) => response.data || response,
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        { type: 'Route', id: `stops-${routeId}` },
        'Cargo',
        'Application'
      ]
    }),

    // Получить маршрут по ID
    getRouteById: builder.query<Route, number>({
      query: (id) => ({
        url: `/routes/${id}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<Route>) => response.data!,
      providesTags: (result, error, id: number) => [{ type: 'Route', id }]
    }),

    getRouteView: builder.query<RouteViewModel, number>({
      query: (id) => ({
        url: `/routes/${id}/view`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<RouteViewModel>) => response.data!,
      providesTags: (result, error, id: number) => [{ type: 'Route', id }]
    }),

    // Execution orchestration session (Phase B)
    startExecutionSession: builder.mutation<
      { sessionId: string; lifecycleState: string },
      {
        executionSnapshot?: Record<string, unknown>;
        deferredAssignment?: {
          driverId?: number;
          vehicleId?: number;
          trailerVehicleId?: number;
          carrierId?: number;
        };
      }
    >({
      query: (body) => ({
        url: '/routes/execution/sessions',
        method: 'POST',
        body
      }),
      transformResponse: (response: ApiResponse<{ sessionId: string; lifecycleState: string }>) =>
        response.data!
    }),

    rollbackExecutionSession: builder.mutation<
      { sessionId: string; lifecycleState: string; rollbackCompleted: boolean },
      { sessionId: string; reason?: string }
    >({
      query: ({ sessionId, reason }) => ({
        url: `/routes/execution/sessions/${sessionId}/rollback`,
        method: 'POST',
        body: reason ? { reason } : undefined
      }),
      transformResponse: (
        response: ApiResponse<{ sessionId: string; lifecycleState: string; rollbackCompleted: boolean }>
      ) => response.data!,
      invalidatesTags: () => routeDeleteReadModelInvalidatesTags()
    }),

    // Создать новый маршрут
    createRoute: builder.mutation<Route, CreateRouteRequest>({
      query: (data) => ({
        url: '/routes',
        method: 'POST',
        body: data
      }),
      transformResponse: (response: ApiResponse<Route>) => response.data!,
      invalidatesTags: ['Route', 'Cargo', 'Application', 'CargoHistory', notificationListTag],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(notificationsApi.util.invalidateTags([notificationListTag]));
        } catch {
          /* mutation failed */
        }
      },
    }),

    // Обновить маршрут
    updateRoute: builder.mutation<
      Route,
      {
        id: number;
        expectedUpdatedAt?: string;
        mutationReason?: string;
        confirmationProvided?: boolean;
      } & Partial<CreateRouteRequest>
    >({
      query: ({ id, ...data }) => ({
        url: `/routes/${id}`,
        method: 'PUT',
        body: data
      }),
      transformResponse: (response: ApiResponse<Route>) => response.data!,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Route', id },
        'Route',
        'Cargo',
        'Application',
        'CargoHistory'
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(notificationsApi.util.invalidateTags([notificationListTag]));
        } catch {
          /* mutation failed */
        }
      },
    }),

    // Обновить статус маршрута
    updateRouteStatus: builder.mutation<Route, { 
      id: number; 
      status: string; 
      actualDate?: string; 
      notes?: string; 
    }>({
      query: ({ id, ...data }) => ({
        url: `/routes/${id}/status`,
        method: 'PATCH',
        body: data
      }),
      transformResponse: (response: ApiResponse<Route>) => response.data!,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Route', id },
        'Route',
        'Cargo',
        'Application',
        'CargoHistory'
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(notificationsApi.util.invalidateTags([notificationListTag]));
        } catch {
          /* mutation failed */
        }
      },
    }),

    /** Фиксация штрафа перевозчика по маршруту (до/после смены статуса). */
    createRoutePenalty: builder.mutation<
      { id: number },
      {
        routeId: number;
        agreementPenaltyId: number;
        days?: number;
        comment?: string;
        amount?: number;
      }
    >({
      query: ({ routeId, ...body }) => ({
        url: `/routes/${routeId}/penalties`,
        method: 'POST',
        body
      }),
      transformResponse: (response: ApiResponse<{ id: number }>) => response.data!,
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        'Route'
      ]
    }),

    getRouteOperationalProcesses: builder.query<RouteOperationalProcessesPayload, number>({
      query: (routeId) => ({
        url: `/routes/${routeId}/operational-processes`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<RouteOperationalProcessesPayload>) => response.data!,
      providesTags: (result, error, routeId) => [{ type: 'Route', id: routeId }]
    }),

    getMyOperationalProcesses: builder.query<ProcessQueuePayload, void>({
      query: () => ({ url: '/operational-processes/my', method: 'GET' }),
      transformResponse: (response: ApiResponse<ProcessQueuePayload>) => response.data!,
      providesTags: ['Route']
    }),

    getOverdueOperationalProcesses: builder.query<
      ProcessQueuePayload,
      { scopeAll?: boolean } | void
    >({
      query: (args) => ({
        url: '/operational-processes/overdue',
        method: 'GET',
        params: args && 'scopeAll' in args && args.scopeAll ? { scope: 'all' } : undefined
      }),
      transformResponse: (response: ApiResponse<ProcessQueuePayload>) => response.data!,
      providesTags: ['Route']
    }),

    getBlockingOperationalProcesses: builder.query<
      ProcessQueuePayload,
      { scopeAll?: boolean } | void
    >({
      query: (args) => ({
        url: '/operational-processes/blocking',
        method: 'GET',
        params: args && 'scopeAll' in args && args.scopeAll ? { scope: 'all' } : undefined
      }),
      transformResponse: (response: ApiResponse<ProcessQueuePayload>) => response.data!,
      providesTags: ['Route']
    }),

    getOperationalInboxSummary: builder.query<OperationalInboxSummary, void>({
      query: () => ({ url: '/operational-inbox/summary', method: 'GET' }),
      transformResponse: (response: ApiResponse<OperationalInboxSummary>) => response.data!,
      providesTags: ['Route']
    }),

    getOperationalInboxProcesses: builder.query<
      { processes: RouteOperationalProcessesPayload['processes']; queue: string },
      { queue?: string } | void
    >({
      query: (args) => ({
        url: '/operational-inbox/processes',
        method: 'GET',
        params: args && 'queue' in args && args.queue ? { queue: args.queue } : undefined
      }),
      transformResponse: (response: ApiResponse<{ processes: RouteOperationalProcessesPayload['processes']; queue: string }>) =>
        response.data!,
      providesTags: ['Route']
    }),

    getOperationalInboxOverdue: builder.query<
      { processes: RouteOperationalProcessesPayload['processes']; queue: string },
      void
    >({
      query: () => ({ url: '/operational-inbox/overdue', method: 'GET' }),
      transformResponse: (response: ApiResponse<{ processes: RouteOperationalProcessesPayload['processes']; queue: string }>) =>
        response.data!,
      providesTags: ['Route']
    }),

    getOperationalCompletion: builder.query<
      { snapshot: OperationalCompletionSnapshotPayload | null; locked: boolean },
      number
    >({
      query: (routeId) => ({
        url: `/routes/${routeId}/operational-completion`,
        method: 'GET'
      }),
      transformResponse: (
        response: ApiResponse<{ snapshot: OperationalCompletionSnapshotPayload | null; locked: boolean }>
      ) => response.data!,
      providesTags: (result, error, routeId) => [{ type: 'Route', id: routeId }]
    }),

    finalizeOperationalCompletion: builder.mutation<
      {
        snapshot: OperationalCompletionSnapshotPayload;
        locked: boolean;
        successorControlTaskId?: number;
        successorTaskRecovered?: boolean;
      },
      {
        routeId: number;
        logistComment: string;
        penaltyIds?: number[];
        expenseIds?: number[];
        warnings?: string[];
        origin?: 'ROUTE_CORRECTION';
        correctionCompletionReason?: 'CARRIER_REPLACEMENT';
      }
    >({
      query: ({ routeId, ...body }) => ({
        url: `/routes/${routeId}/operational-completion/finalize`,
        method: 'POST',
        body
      }),
      transformResponse: (
        response: ApiResponse<{
          snapshot: OperationalCompletionSnapshotPayload;
          locked: boolean;
          successorControlTaskId?: number;
          successorTaskRecovered?: boolean;
        }>
      ) => response.data!,
      invalidatesTags: (result, error, { routeId }) => [{ type: 'Route', id: routeId }, 'Route']
    }),

    // Отметить получение оригиналов ТН
    markOriginalsReceived: builder.mutation<void, {
      routeId: number;
      receivedDate: string;
      method: 'COURIER' | 'MAIL' | 'HAND';
      comment?: string;
    }>({
      query: ({ routeId, receivedDate, method, comment }) => ({
        url: `/routes/${routeId}/originals-received`,
        method: 'POST',
        body: { receivedDate, method, comment }
      }),
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        'Route',
        'Workspace'
      ]
    }),

    // Удалить маршрут
    deleteRoute: builder.mutation<void, number>({
      query: (id) => ({
        url: `/routes/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => routeDeleteReadModelInvalidatesTags(id)
    }),

    // Получить события маршрута
    getRouteEvents: builder.query<RouteEvent[], number>({
      query: (routeId) => ({
        url: `/routes/${routeId}/events`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<RouteEvent[]>) => response.data || [],
      providesTags: (result, error, routeId: number) => [
        { type: 'Route', id: `events-${routeId}` }
      ]
    }),

    // Добавить событие к маршруту
    addRouteEvent: builder.mutation<RouteEvent, {
      routeId: number;
      eventType: string;
      description?: string;
      locationLat?: number;
      locationLng?: number;
      eventDate?: string;
    }>({
      query: ({ routeId, ...data }) => ({
        url: `/routes/${routeId}/events`,
        method: 'POST',
        body: data
      }),
      transformResponse: (response: ApiResponse<RouteEvent>) => response.data!,
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        { type: 'Route', id: `events-${routeId}` }
      ]
    }),

    // Получить доступные контрагенты по роли
    getAvailableContractors: builder.query<Contractor[], string>({
      query: (role) => ({
        url: `/routes/contractors/available?role=${role}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<Contractor[]>) => response.data || [],
      providesTags: ['Contractor']
    }),

    // Получить доступные грузы для точки погрузки
    getAvailableCargo: builder.query<ApplicationCargo[], {
      loadingPointType: 'warehouse' | 'shipper' | 'application';
      loadingPointId?: number;
      /** При редактировании маршрута — аллокации этого маршрута не уменьшают доступный остаток. */
      excludeRouteId?: number;
    }>({
      query: ({ loadingPointType, loadingPointId, excludeRouteId }) => {
        const params = new URLSearchParams();
        if (loadingPointType) {
          params.append('loadingPointType', loadingPointType);
        }
        if (loadingPointId != null && Number.isFinite(Number(loadingPointId))) {
          params.append('loadingPointId', String(loadingPointId));
        }
        if (excludeRouteId != null && Number.isFinite(Number(excludeRouteId)) && Number(excludeRouteId) > 0) {
          params.append('excludeRouteId', String(excludeRouteId));
        }

        return {
          url: `/routes/cargo/available?${params.toString()}`,
          method: 'GET'
        };
      },
      transformResponse: (response: ApiResponse<ApplicationCargo[]>) => response.data || [],
      providesTags: ['Cargo']
    }),

    // Маршруты с операционными проблемами (контрольные задачи OPEN/OVERDUE)
    getRoutesWithIssues: builder.query<any[], void>({
      query: () => '/routes/issues',
      transformResponse: (response: ApiResponse<any[]>) => response.data || [],
      providesTags: ['Route']
    }),

    // Получить маршруты по заявке
    getRoutesByApplication: builder.query<Route[], number>({
      query: (applicationId) => `/applications/${applicationId}/routes`,
      transformResponse: (response: any) => response.data || response,
      providesTags: (result, error, applicationId) => [
        { type: 'Route', id: `application-${applicationId}` },
        { type: 'Application', id: applicationId }
      ],
    }),

    // Получить список заявок для фильтра
    getApplicationsForFilter: builder.query<{applications: any[], pagination: any}, {page?: number, limit?: number, search?: string}>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.limit) searchParams.append('limit', params.limit.toString());
        if (params.search) searchParams.append('search', params.search);
        
        return {
          url: `/applications?${searchParams.toString()}`,
          method: 'GET'
        };
      },
      transformResponse: (response: ApiResponse<{applications: any[], pagination: any}>) => response.data!,
      providesTags: ['Application']
    }),

    // Создать аллокации (выделение количества мест для грузов в маршрут). Сервер валидирует cargoId по заявкам маршрута.
    createAllocations: builder.mutation<
      any,
      {
        routeId: number;
        allocations: { cargoId: number; quantity: number; fromStopId?: number; toStopId?: number }[];
        executionSnapshot?: Record<string, unknown>;
        finalizeRouteCreate?: boolean;
        executionSessionId?: string;
        mutationReason?: string;
        confirmationProvided?: boolean;
      }
    >({
      query: ({
        routeId,
        allocations,
        executionSnapshot,
        finalizeRouteCreate,
        executionSessionId,
        mutationReason,
        confirmationProvided
      }) => ({
        url: `/routes/${routeId}/allocations`,
        method: 'POST',
        body: {
          items: allocations.map((a) => ({
            cargoId: a.cargoId,
            allocatedQuantity: a.quantity,
            fromStopId: a.fromStopId,
            toStopId: a.toStopId,
          })),
          ...(executionSnapshot ? { executionSnapshot } : {}),
          ...(finalizeRouteCreate ? { finalizeRouteCreate: true } : {}),
          ...(executionSessionId ? { executionSessionId } : {}),
          ...(mutationReason ? { mutationReason } : {}),
          ...(confirmationProvided ? { confirmationProvided: true } : {})
        },
      }),
      transformResponse: (response: ApiResponse<any>) => response.data!,
      invalidatesTags: (result: any, error: any, { routeId }: { routeId: number }) => [
        { type: 'Route', id: routeId },
        'Route',
        'Cargo',
        'Application',
        'CargoHistory'
      ]
    }),

    // Получить аллокации маршрута
    getAllocations: builder.query<any[], number>({
      query: (routeId: number) => ({
        url: `/routes/${routeId}/allocations`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<any[]>) => response.data || [],
      providesTags: (result: any, error: any, routeId: number) => [
        { type: 'Route', id: routeId },
        'Route',
        'Cargo'
      ]
    }),

    getExecutionProgress: builder.query<ExecutionProgressHydration, number>({
      query: (routeId) => ({
        url: `/routes/${routeId}/execution/progress`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<ExecutionProgressHydration>) => response.data!,
      providesTags: (result, error, routeId) => [{ type: 'Route', id: `execution-${routeId}` }]
    }),

    getExecutionDashboard: builder.query<ExecutionDashboardSlice, number>({
      query: (routeId) => ({
        url: `/routes/${routeId}/execution/dashboard`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<ExecutionDashboardSlice>) => response.data!,
      providesTags: (result, error, routeId) => [{ type: 'Route', id: `execution-dash-${routeId}` }]
    }),

    getExecutionEvents: builder.query<ExecutionEventRow[], number>({
      query: (routeId) => ({
        url: `/routes/${routeId}/execution/events?limit=50`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<ExecutionEventRow[]>) => response.data || [],
      providesTags: (result, error, routeId) => [{ type: 'Route', id: `execution-events-${routeId}` }]
    }),

    applyStopExecutionAction: builder.mutation<
      { stop: unknown; progress: unknown },
      {
        routeId: number;
        executionStopUid: string;
        action: StopExecutionAction;
        notes?: string;
        occurredAt?: string;
        cargoQuantities?: CargoQuantityUpdate[];
      }
    >({
      query: ({ routeId, executionStopUid, action, notes, occurredAt, cargoQuantities }) => ({
        url: `/routes/${routeId}/execution/stops/${encodeURIComponent(executionStopUid)}/actions`,
        method: 'POST',
        body: { action, notes, occurredAt, cargoQuantities }
      }),
      transformResponse: (response: ApiResponse<{ stop: unknown; progress: unknown }>) => response.data!,
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        { type: 'Route', id: `execution-${routeId}` },
        { type: 'Route', id: `execution-dash-${routeId}` },
        { type: 'Route', id: `execution-events-${routeId}` }
      ]
    }),

    getExecutionIncidents: builder.query<ExecutionIncident[], number>({
      query: (routeId) => ({
        url: `/routes/${routeId}/execution/incidents`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<ExecutionIncident[]>) => response.data || [],
      providesTags: (result, error, routeId) => [{ type: 'Route', id: `execution-incidents-${routeId}` }]
    }),

    createExecutionIncident: builder.mutation<
      ExecutionIncident,
      {
        routeId: number;
        incidentType: ExecutionIncident['incidentType'];
        comment?: string;
        relatedExecutionStopUid?: string;
        blockingFlag?: boolean;
        downtimeHours?: number;
        extraCost?: number;
        occurredAt?: string;
      }
    >({
      query: ({ routeId, ...body }) => ({
        url: `/routes/${routeId}/execution/incidents`,
        method: 'POST',
        body
      }),
      transformResponse: (response: ApiResponse<ExecutionIncident>) => response.data!,
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        { type: 'Route', id: `execution-incidents-${routeId}` },
        { type: 'Route', id: `execution-events-${routeId}` }
      ]
    }),

    resolveExecutionIncident: builder.mutation<
      ExecutionIncident,
      { routeId: number; incidentId: number; resolutionComment?: string }
    >({
      query: ({ routeId, incidentId, resolutionComment }) => ({
        url: `/routes/${routeId}/execution/incidents/${incidentId}`,
        method: 'PATCH',
        body: { resolutionComment }
      }),
      transformResponse: (response: ApiResponse<ExecutionIncident>) => response.data!,
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        { type: 'Route', id: `execution-incidents-${routeId}` },
        { type: 'Route', id: `execution-events-${routeId}` }
      ]
    }),

    confirmRecoveryDelay: builder.mutation<
      { confirmation: unknown; hydration: ExecutionProgressHydration },
      { routeId: number; reason?: string }
    >({
      query: ({ routeId, reason }) => ({
        url: `/routes/${routeId}/recovery/confirm-delay`,
        method: 'POST',
        body: { reason }
      }),
      transformResponse: (
        response: ApiResponse<{ confirmation: unknown; hydration: ExecutionProgressHydration }>
      ) => response.data!,
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        { type: 'Route', id: `execution-${routeId}` },
        { type: 'Route', id: `recovery-${routeId}` }
      ]
    }),

    confirmRecoveryDowntime: builder.mutation<
      { confirmation: unknown; hydration: ExecutionProgressHydration },
      { routeId: number; reason?: string }
    >({
      query: ({ routeId, reason }) => ({
        url: `/routes/${routeId}/recovery/confirm-downtime`,
        method: 'POST',
        body: { reason }
      }),
      transformResponse: (
        response: ApiResponse<{ confirmation: unknown; hydration: ExecutionProgressHydration }>
      ) => response.data!,
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        { type: 'Route', id: `execution-${routeId}` },
        { type: 'Route', id: `recovery-${routeId}` }
      ]
    }),

    resumeRouteRecovery: builder.mutation<
      { confirmation: unknown; hydration: ExecutionProgressHydration },
      { routeId: number; reason?: string }
    >({
      query: ({ routeId, reason }) => ({
        url: `/routes/${routeId}/recovery/resume`,
        method: 'POST',
        body: { reason }
      }),
      transformResponse: (
        response: ApiResponse<{ confirmation: unknown; hydration: ExecutionProgressHydration }>
      ) => response.data!,
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        { type: 'Route', id: `execution-${routeId}` },
        { type: 'Route', id: `recovery-${routeId}` }
      ]
    }),

    acknowledgeRouteRecovery: builder.mutation<
      { confirmation: unknown; hydration: ExecutionProgressHydration },
      { routeId: number; reason?: string }
    >({
      query: ({ routeId, reason }) => ({
        url: `/routes/${routeId}/recovery/acknowledge`,
        method: 'POST',
        body: { reason }
      }),
      transformResponse: (
        response: ApiResponse<{ confirmation: unknown; hydration: ExecutionProgressHydration }>
      ) => response.data!,
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        { type: 'Route', id: `execution-${routeId}` },
        { type: 'Route', id: `recovery-${routeId}` }
      ]
    }),

    /** RTE-X11.5b — applications eligible for one-click route injection */
    getInjectableApplications: builder.query<
      Array<{
        id: number;
        internalNumber: string;
        clientName: string | null;
        routeLabel: string;
        cargoSummary: string;
        totalWeightKg: number | null;
        totalVolumeM3: number | null;
        cargoCount: number;
        availableCargoCount: number;
      }>,
      { routeId: number; search?: string; limit?: number }
    >({
      query: ({ routeId, search, limit }) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (limit != null) params.append('limit', String(limit));
        const qs = params.toString();
        return {
          url: `/routes/${routeId}/injectable-applications${qs ? `?${qs}` : ''}`,
          method: 'GET'
        };
      },
      transformResponse: (response: ApiResponse<any[]>) => response.data || []
    }),

    injectApplicationIntoRoute: builder.mutation<
      {
        routeId: number;
        applicationId: number;
        loadingStopId: number;
        unloadingStopId: number;
        cargoIds: number[];
        allocationCount: number;
      },
      {
        routeId: number;
        applicationId: number;
        insertAfterPosition?: number | null;
        confirmationProvided?: boolean;
        mutationReason?: string;
      }
    >({
      query: ({ routeId, ...body }) => ({
        url: `/routes/${routeId}/inject-application`,
        method: 'POST',
        body
      }),
      transformResponse: (response: ApiResponse<any>) => response.data!,
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'Route', id: routeId },
        { type: 'Route', id: `view-${routeId}` },
        'Route',
        'Cargo',
        'Application'
      ]
    })
  })
});

export const {
  useGetAllRoutesQuery,
  useLazyGetAllRoutesQuery,
  useGetRouteByIdQuery,
  useLazyGetRouteByIdQuery,
  useGetRouteViewQuery,
  useCreateRouteMutation,
  useStartExecutionSessionMutation,
  useRollbackExecutionSessionMutation,
  useUpdateRouteMutation,
  useUpdateRouteStatusMutation,
  useCreateRoutePenaltyMutation,
  useGetRouteOperationalProcessesQuery,
  useGetMyOperationalProcessesQuery,
  useGetOverdueOperationalProcessesQuery,
  useGetBlockingOperationalProcessesQuery,
  useGetOperationalInboxSummaryQuery,
  useGetOperationalInboxProcessesQuery,
  useGetOperationalInboxOverdueQuery,
  useGetOperationalCompletionQuery,
  useFinalizeOperationalCompletionMutation,
  useMarkOriginalsReceivedMutation,
  useDeleteRouteMutation,
  useGetRouteEventsQuery,
  useAddRouteEventMutation,
  useGetAvailableContractorsQuery,
  useGetAvailableCargoQuery,
  useGetRoutesByApplicationQuery,
  useGetApplicationsForFilterQuery,
  useCreateAllocationsMutation,
  useGetAllocationsQuery,
  useGetRouteStopsQuery,
  useUpsertRouteStopsMutation,
  useDeleteRouteStopMutation,
  useLoadRouteStopMutation,
  useUnloadRouteStopMutation,
  useGetRoutesWithIssuesQuery,
  useGetExecutionProgressQuery,
  useGetExecutionDashboardQuery,
  useGetExecutionEventsQuery,
  useApplyStopExecutionActionMutation,
  useGetExecutionIncidentsQuery,
  useCreateExecutionIncidentMutation,
  useResolveExecutionIncidentMutation,
  useConfirmRecoveryDelayMutation,
  useConfirmRecoveryDowntimeMutation,
  useResumeRouteRecoveryMutation,
  useAcknowledgeRouteRecoveryMutation,
  useGetInjectableApplicationsQuery,
  useInjectApplicationIntoRouteMutation
} = routesApi; 